/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  DialogContentProps,
  FORM_THEME_LITE,
  Form,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import { COMPONENT_SERVICE_STRINGS } from 'apps/component-service/src/Constants/Strings.constants';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import {
  ComponentContentServicePostMatrixIdentifierSettingsDto,
  getComponentContentService,
} from '../../../../Services/component-content';
import {
  CONTENT_MATRIX_IDENTIFIER_DATA_IDS,
  CONTENT_MATRIX_IDENTIFIER_FORM_IDS,
} from '../../ContentMatrixIdentifiers.constants';

/**
 * The form for creating a new matrix identifier.
 */
export const CreateContentMatrixIdentifierForm = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { setButtonKeys, setButtons, close } = props;

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [successDone, setSuccessDone] = useState<boolean>(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const apiKeyRef = useRef<HTMLInputElement>(null);
  const apiUrlRef = useRef<HTMLInputElement>(null);

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  // Component content service
  const componentContentService = getComponentContentService();

  // Labels
  const labels = useMemo(() => {
    return {
      alreadyExists: t(COMPONENT_SERVICE_STRINGS.matrixIdentifierErrorAlreadyExists),
      apiKey: t(COMPONENT_SERVICE_STRINGS.matrixIdentifierPropApiKey),
      apiUrl: t(COMPONENT_SERVICE_STRINGS.matrixIdentifierPropApiUrl),
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      create: t(COMPONENT_SERVICE_STRINGS.matrixIdentifierActionCreate),
      failure: t(COMPONENT_SERVICE_STRINGS.apiError),
      name: t(COMPONENT_SERVICE_STRINGS.matrixIdentifierPropName),
      required: t(COMPONENT_SERVICE_STRINGS.requiredField),
    };
  }, [t]);

  // Form input values
  const inputValues = { apiKey: ``, apiUrl: ``, name: `` };

  // Required form inputs
  const requiredInputs = useMemo(() => {
    return [
      CONTENT_MATRIX_IDENTIFIER_FORM_IDS.name,
      CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiUrl,
      CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiKey,
    ];
  }, []);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };

      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;

        // Check that the input fields are valid
        if (!notEmptyValidator.validate(inputValue) && requiredInputs.includes(inputFieldId)) {
          nextErrors[inputFieldId] = labels.required;
        } else {
          delete nextErrors[inputFieldId];
        }
      });

      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator, requiredInputs],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const submitHandler = useCallback(() => {
    const inputs = {
      inputApiKey: apiKeyRef.current as HTMLInputElement,
      inputApiUrl: apiUrlRef.current as HTMLInputElement,
      inputName: nameRef.current as HTMLInputElement,
    };

    // Validate the required input fields
    if (
      !notEmptyValidator.validate(inputs.inputName.value) ||
      !notEmptyValidator.validate(inputs.inputApiUrl.value) ||
      !notEmptyValidator.validate(inputs.inputApiKey.value)
    ) {
      validateFields([inputs.inputName, inputs.inputApiUrl, inputs.inputApiKey]);
      setSubmit(false);
      return;
    }

    // Reset any form errors
    setFormError(``);

    // Construct the DTO for the create set request
    const contentMatrixIdentifierDto: ComponentContentServicePostMatrixIdentifierSettingsDto = {
      apiKey: inputs.inputApiKey.value.trim(),
      apiUrl: inputs.inputApiUrl.value.trim(),
      name: inputs.inputName.value.trim(),
    };

    // Send request to create component set
    lastValueFrom(componentContentService.createMatrixIdentifier(contentMatrixIdentifierDto))
      .then(() => {
        // Close modal & set success to true
        setSuccessDone(true);
        return lastValueFrom(componentContentService.getAllMatrixIdentifiers());
      })
      .then(() => {
        close();
        return true;
      })
      .catch((err) => {
        // Check what error occurred
        const errorMessage: string = err?.response?.message || '';
        // Component set already exists
        if (errorMessage.indexOf('already exists') > -1) {
          setFormError(labels.alreadyExists);
        } else {
          setFormError(labels.failure);
        }
        setSubmit(false);
      });
  }, [close, componentContentService, labels, notEmptyValidator, validateFields]);

  useEffect(() => {
    const cancelButton = (
      <Button
        dataId={CONTENT_MATRIX_IDENTIFIER_DATA_IDS.createIdentifierCancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const createButton = (
      <Button
        dataId={CONTENT_MATRIX_IDENTIFIER_DATA_IDS.createIdentifierConfirm}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.create}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    // Check for form submission
    if (isFormSubmitted && !successDone) {
      setButtonKeys([`create`]);
      setButtons([createButton]);
      submitHandler();
    } else {
      setButtonKeys([`cancel`, `create`]);
      setButtons([cancelButton, createButton]);
    }
  }, [close, isFormSubmitted, labels, setButtonKeys, setButtons, submitHandler, successDone]);

  const nameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[CONTENT_MATRIX_IDENTIFIER_FORM_IDS.name] || null,
    fieldLabel: labels.name,
    id: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.name,
    name: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.name,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    value: inputValues.name,
  };

  const apiKeyFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiKey] || null,
    fieldLabel: labels.apiKey,
    id: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiKey,
    name: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiKey,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    value: inputValues.apiKey,
  };

  const apiUrlFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiUrl] || null,
    fieldLabel: labels.apiUrl,
    id: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiUrl,
    name: CONTENT_MATRIX_IDENTIFIER_FORM_IDS.apiUrl,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    value: inputValues.apiUrl,
  };

  return (
    <div className="component-matrix-identifiers__create-matrix-identifier-content">
      <Form
        classes="component-matrix-identifiers__create-matrix-identifier-form"
        errorMessage={formError}
        legend={labels.create}
        theme={FORM_THEME_LITE}
      >
        <TextField
          {...nameFieldProps}
          data-id={CONTENT_MATRIX_IDENTIFIER_DATA_IDS.createIdentifierName}
          ref={nameRef}
        />

        <TextField
          {...apiKeyFieldProps}
          data-id={CONTENT_MATRIX_IDENTIFIER_DATA_IDS.createIdentifierApiKey}
          note={!apiKeyFieldProps.errorMessage ? labels.apiKey : undefined}
          ref={apiKeyRef}
        />

        <TextField
          {...apiUrlFieldProps}
          data-id={CONTENT_MATRIX_IDENTIFIER_DATA_IDS.createIdentifierApiUrl}
          ref={apiUrlRef}
        />
      </Form>
    </div>
  );
};
